import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Button } from "../components/Button";
import { Card } from "../components/Card";
import { Field } from "../components/Field";
import { useToast } from "../components/Toast";
import { apiGet, apiSend } from "../lib/customFetch";
import styles from "./KioskAdminCard.module.css";

interface KioskEmployee {
  id: string;
  navn: string;
  codeSet: boolean;
}

interface KioskSession {
  id: string;
  label: string;
  createdAt: string;
  expiresAt: string;
  lastSeenAt: string;
  revokedAt: string | null;
}

interface Pairing {
  label: string;
  pairingUrl: string;
  expiresAt: string;
}

function dato(iso: string): string {
  return new Date(iso).toLocaleString("da-DK", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function KioskAdminCard() {
  const toast = useToast();
  const [employees, setEmployees] = useState<KioskEmployee[] | null>(null);
  const [sessions, setSessions] = useState<KioskSession[] | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [label, setLabel] = useState("");
  const [pairing, setPairing] = useState<Pairing | null>(null);
  const [pairingBusy, setPairingBusy] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [codeBusy, setCodeBusy] = useState(false);

  const load = useCallback(() => {
    setLoadFailed(false);
    Promise.all([
      apiGet<KioskEmployee[]>("/api/kiosk/admin/employees"),
      apiGet<KioskSession[]>("/api/kiosk/admin/sessions"),
    ])
      .then(([nextEmployees, nextSessions]) => {
        setEmployees(nextEmployees);
        setSessions(nextSessions);
      })
      .catch(() => setLoadFailed(true));
  }, []);

  useEffect(load, [load]);

  const createPairing = (event: FormEvent) => {
    event.preventDefault();
    if (label.trim().length === 0) return;
    setPairingBusy(true);
    apiSend<Pairing>("POST", "/api/kiosk/admin/pairings", { label: label.trim() })
      .then((created) => {
        setPairingBusy(false);
        setPairing(created);
        setLabel("");
      })
      .catch(() => {
        setPairingBusy(false);
        toast.show("Kunne ikke oprette engangslinket.", "error");
      });
  };

  const saveCode = (employee: KioskEmployee, nextCode: string | null) => {
    if (nextCode !== null && !/^\d{4}$/.test(nextCode)) return;
    setCodeBusy(true);
    apiSend<{ id: string; codeSet: boolean }>(
      "PATCH",
      `/api/kiosk/admin/employees/${employee.id}/code`,
      { code: nextCode },
    )
      .then((result) => {
        setCodeBusy(false);
        setEmployees((current) =>
          current?.map((item) =>
            item.id === result.id ? { ...item, codeSet: result.codeSet } : item,
          ) ?? null,
        );
        setEditingId(null);
        setCode("");
        toast.show(nextCode === null ? "Kioskkoden er fjernet." : "Kioskkoden er gemt.", "success");
      })
      .catch(() => {
        setCodeBusy(false);
        toast.show("Kunne ikke gemme kioskkoden.", "error");
      });
  };

  const revoke = (session: KioskSession) => {
    if (!window.confirm(`Tilbagekald ${session.label}? Kiosken mister straks adgang.`)) return;
    apiSend("DELETE", `/api/kiosk/admin/sessions/${session.id}`)
      .then(() => {
        setSessions((current) =>
          current?.map((item) =>
            item.id === session.id ? { ...item, revokedAt: new Date().toISOString() } : item,
          ) ?? null,
        );
        toast.show("Kioskenheden er tilbagekaldt.", "success");
      })
      .catch(() => toast.show("Kunne ikke tilbagekalde kioskenheden.", "error"));
  };

  return (
    <Card title="Kiosk">
      <div className={styles.content}>
        <p className={styles.hint}>
          Brug en fælles tablet eller skærm til ind- og udstempling. Opret først
          personlige firecifrede koder og par derefter skærmen med et engangslink.
        </p>

        {loadFailed ? (
          <div className={styles.error} role="alert">
            Kunne ikke hente kioskopsætningen. <Button variant="ghost" onClick={load}>Prøv igen</Button>
          </div>
        ) : employees === null || sessions === null ? (
          <p className={styles.hint}>Henter kioskopsætning…</p>
        ) : (
          <>
            <section className={styles.section}>
              <h3>Medarbejderkoder</h3>
              {employees.length === 0 ? (
                <p className={styles.hint}>Der er ingen aktive stempelmedarbejdere.</p>
              ) : (
                <ul className={styles.list}>
                  {employees.map((employee) => (
                    <li className={styles.row} key={employee.id}>
                      <div>
                        <strong>{employee.navn}</strong>
                        <span className={employee.codeSet ? styles.ready : styles.missing}>
                          {employee.codeSet ? "Kode opsat" : "Mangler kode"}
                        </span>
                      </div>
                      {editingId === employee.id ? (
                        <form
                          className={styles.codeForm}
                          onSubmit={(event) => {
                            event.preventDefault();
                            saveCode(employee, code);
                          }}
                        >
                          <label>
                            <span className="visually-hidden">Ny kode til {employee.navn}</span>
                            <input
                              aria-label={`Ny kode til ${employee.navn}`}
                              inputMode="numeric"
                              autoComplete="off"
                              pattern="[0-9]{4}"
                              maxLength={4}
                              value={code}
                              onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 4))}
                              autoFocus
                            />
                          </label>
                          <Button type="submit" disabled={codeBusy || code.length !== 4}>Gem</Button>
                          <Button variant="ghost" onClick={() => { setEditingId(null); setCode(""); }}>Annullér</Button>
                        </form>
                      ) : (
                        <div className={styles.actions}>
                          <Button variant="secondary" onClick={() => { setEditingId(employee.id); setCode(""); }}>
                            {employee.codeSet ? "Skift kode" : "Sæt kode"}
                          </Button>
                          {employee.codeSet && (
                            <Button variant="ghost" onClick={() => saveCode(employee, null)}>Fjern</Button>
                          )}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className={styles.section}>
              <h3>Par en ny skærm</h3>
              <form className={styles.pairForm} onSubmit={createPairing}>
                <Field
                  label="Navn på skærmen"
                  value={label}
                  maxLength={100}
                  placeholder="Fx Værkstedets tablet"
                  onChange={(event) => setLabel(event.target.value)}
                />
                <Button type="submit" disabled={pairingBusy || label.trim().length === 0}>
                  {pairingBusy ? "Opretter…" : "Opret engangslink"}
                </Button>
              </form>
              {pairing !== null && (
                <div className={styles.pairing} role="status">
                  <strong>Åbn dette link på {pairing.label}</strong>
                  <a href={pairing.pairingUrl}>{`${window.location.origin}${pairing.pairingUrl}`}</a>
                  <span>Linket udløber {dato(pairing.expiresAt)} og kan kun bruges én gang.</span>
                </div>
              )}
            </section>

            <section className={styles.section}>
              <h3>Parrede skærme</h3>
              {sessions.length === 0 ? (
                <p className={styles.hint}>Ingen skærme er parret endnu.</p>
              ) : (
                <ul className={styles.list}>
                  {sessions.map((session) => (
                    <li className={styles.row} key={session.id}>
                      <div>
                        <strong>{session.label}</strong>
                        <span className={session.revokedAt === null ? styles.ready : styles.missing}>
                          {session.revokedAt === null ? `Aktiv til ${dato(session.expiresAt)}` : "Tilbagekaldt"}
                        </span>
                      </div>
                      {session.revokedAt === null && (
                        <Button variant="destructive" onClick={() => revoke(session)}>Tilbagekald</Button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </>
        )}
      </div>
    </Card>
  );
}
